import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import firebase from 'firebase';
import { toast } from 'react-toastify';

import SideBar from '../../components/sidebar';
import { firebaseConfig } from '../../constants/Config';
import Header from '../../components/Header';
import { EDColors } from '../../constants/Colors';
import styles from './style';

const TITLE_MAX_LENGTH = 65;
const BODY_MAX_LENGTH = 240;
const LINK_MAX_LENGTH = 2048;
const CONFIRMATION_PHRASE = 'SEND';

// No account index (/u/N) in the URL, so the console opens with whichever Google account the admin is signed in to.
const FIREBASE_MESSAGING_URL = `https://console.firebase.google.com/project/${firebaseConfig.projectId}/messaging`;

const openFirebaseMessaging = () => window.open(FIREBASE_MESSAGING_URL, '_blank', 'noopener,noreferrer');

const callFunction = (name, data) => firebase.functions().httpsCallable(name)(data);

const createRequestId = () =>
  `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

// Matches the server rule: an optional full http(s) address. Returns the escaped form the
// server stores, '' for no link, or null when the link is invalid.
const normalizeLink = (link) => {
  if (!link) {
    return '';
  }
  try {
    const url = new URL(link);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch (e) {
    return null;
  }
};

const formatDate = (millis) => (millis ? new Date(millis).toLocaleString() : '—');

const ActionButton = ({ text, onPress, disabled, variant = 'primary', icon }) => (
  <Pressable
    onPress={onPress}
    disabled={disabled}
    style={[
      styles.button,
      variant === 'danger' && styles.buttonDanger,
      variant === 'secondary' && styles.buttonSecondary,
      disabled && styles.buttonDisabled,
    ]}
  >
    {icon && (
      <Ionicons
        name={icon}
        size={16}
        color={variant === 'secondary' ? EDColors.topbar : EDColors.white}
        style={{ marginRight: 8 }}
      />
    )}
    <Text style={[styles.buttonTxt, variant === 'secondary' && styles.buttonTxtSecondary]}>{text}</Text>
  </Pressable>
);

const NotificationPreview = ({ title, body, link }) => (
  <View style={styles.previewCard}>
    <View style={styles.previewHeader}>
      <View style={styles.previewAppIcon} />
      <Text style={styles.previewAppName}>HAM RADIO PREP</Text>
      <Text style={styles.previewTime}>now</Text>
    </View>
    <Text style={styles.previewTitle} numberOfLines={1}>{title || 'Notification title'}</Text>
    <Text style={styles.previewBody} numberOfLines={4}>{body || 'Your message will appear here.'}</Text>
    {!!link && (
      <View style={styles.previewLinkRow}>
        <Ionicons name="link-outline" size={14} color={EDColors.topbar} style={{ marginRight: 4 }} />
        <Text style={styles.previewLink} numberOfLines={1}>Tap opens {link}</Text>
      </View>
    )}
  </View>
);

const Broadcast = ({ navigation }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');

  const [testEmail, setTestEmail] = useState(firebase.auth().currentUser?.email ?? '');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [sending, setSending] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const trimmedTitle = title.trim();
  const trimmedBody = body.trim();
  const normalizedLink = normalizeLink(link.trim());
  const linkValid = normalizedLink !== null;
  const trimmedLink = normalizedLink ?? '';
  const isValid = trimmedTitle.length > 0 && trimmedBody.length > 0 && linkValid;
  const isSameMessage = (item) =>
    item.title === trimmedTitle && item.body === trimmedBody && (item.link || '') === trimmedLink;
  const testedCurrentMessage = testResult?.success && isSameMessage(testResult);
  const duplicate = history.find((item) => item.status === 'sent' && isSameMessage(item));

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await callFunction('adminGetBroadcastNotifications');
      setHistory(res.data ?? []);
    } catch (e) {
      console.log('ERROR WHILE FETCHING BROADCASTS: ', e);
    }
    setHistoryLoading(false);
  };

  const handleSendTest = async () => {
    setTestSending(true);
    setTestResult(null);
    try {
      const res = await callFunction('adminSendTestBroadcastNotification', { title: trimmedTitle, body: trimmedBody, link: trimmedLink, email: testEmail.trim() });
      const { deviceCount, successCount } = res.data;
      setTestResult({
        success: successCount > 0,
        title: trimmedTitle,
        body: trimmedBody,
        link: trimmedLink,
        message: `Delivered to ${successCount} of ${deviceCount} device${deviceCount === 1 ? '' : 's'} for ${testEmail.trim()}.`,
      });
    } catch (e) {
      setTestResult({ success: false, message: e.message });
    }
    setTestSending(false);
  };

  const openConfirm = () => {
    setConfirmation('');
    // A new id per dialog: retries inside the same dialog reuse it, so the server sends at most once.
    setRequestId(createRequestId());
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (sending) {
      return;
    }
    setConfirmOpen(false);
  };

  const handleSendToAll = async () => {
    if (confirmation !== CONFIRMATION_PHRASE || sending) {
      return;
    }
    setSending(true);
    try {
      await callFunction('adminSendBroadcastNotification', { title: trimmedTitle, body: trimmedBody, link: trimmedLink, confirmation, requestId });
      toast.success('Broadcast sent to all users.');
      setConfirmOpen(false);
      setTitle('');
      setBody('');
      setLink('');
      setTestResult(null);
      fetchHistory();
    } catch (e) {
      if (e.code === 'already-exists') {
        // The server already has this request: it went out, or is still going out. Never invite a resend.
        if (e.details?.status === 'sent') {
          toast.info('This broadcast has already been sent. No new notification went out.');
        } else {
          toast.warning(e.message);
        }
        setConfirmOpen(false);
        setTitle('');
        setBody('');
        setLink('');
        setTestResult(null);
        fetchHistory();
      } else {
        // Nothing was sent; the dialog stays open so a retry reuses the same request id.
        toast.error(e.message || 'Unable to send the broadcast.');
      }
    }
    setSending(false);
  };

  return (
    <View style={[styles.container, { flexDirection: 'row' }]}>
      <SideBar navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scrollView}>
        <Header />
        <View style={styles.titleRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.title}>Push Notification</Text>
            <Text style={styles.subtitle}>
              Send a push notification to every user who has the mobile app installed.
            </Text>
          </View>
          <View style={styles.consoleButton}>
            <ActionButton
              text="Open Firebase Messaging"
              icon="open-outline"
              variant="secondary"
              onPress={openFirebaseMessaging}
            />
          </View>
        </View>

        <View style={styles.row}>
          <View style={[styles.card, styles.formCard]}>
            <Text style={styles.label}>Title</Text>
            <TextInput
              style={styles.input}
              value={title}
              onChangeText={setTitle}
              maxLength={TITLE_MAX_LENGTH}
              placeholder="e.g. New practice questions are live"
            />
            <Text style={styles.counter}>{title.length}/{TITLE_MAX_LENGTH}</Text>

            <Text style={styles.label}>Message</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={body}
              onChangeText={setBody}
              maxLength={BODY_MAX_LENGTH}
              multiline
              placeholder="What do you want users to know?"
            />
            <Text style={styles.counter}>{body.length}/{BODY_MAX_LENGTH}</Text>

            <Text style={styles.label}>Link (optional)</Text>
            <TextInput
              style={[styles.input, !linkValid && styles.inputError]}
              value={link}
              onChangeText={setLink}
              maxLength={LINK_MAX_LENGTH}
              placeholder="https://hamradioprep.com/..."
              autoCapitalize="none"
              autoCorrect={false}
            />
            {linkValid ? (
              <Text style={styles.hint}>When users tap the notification, the app opens this link.</Text>
            ) : (
              <Text style={styles.errorTxt}>Enter a full web address starting with https://</Text>
            )}
          </View>

          <View style={[styles.card, styles.previewWrapper]}>
            <Text style={styles.label}>Preview</Text>
            <NotificationPreview title={trimmedTitle} body={trimmedBody} link={trimmedLink} />
            <Text style={styles.hint}>Exact appearance varies by device.</Text>
          </View>
        </View>

        <View style={styles.card}>
          <Text style={styles.stepTitle}>Step 1 · Send a test</Text>
          <Text style={styles.hint}>
            Sends only to the devices of this user, so you can check how it looks before everyone gets it.
          </Text>
          <View style={styles.inlineRow}>
            <TextInput
              style={[styles.input, styles.emailInput]}
              value={testEmail}
              onChangeText={setTestEmail}
              placeholder="user@example.com"
              autoCapitalize="none"
            />
            <ActionButton
              text={testSending ? 'Sending…' : 'Send test'}
              icon="paper-plane-outline"
              variant="secondary"
              onPress={handleSendTest}
              disabled={!isValid || !testEmail.trim() || testSending}
            />
          </View>
          {testResult && (
            <Text style={[styles.result, testResult.success ? styles.resultSuccess : styles.resultError]}>
              {testResult.message}
            </Text>
          )}
        </View>

        <View style={[styles.card, styles.dangerCard]}>
          <Text style={styles.stepTitle}>Step 2 · Send to all users</Text>
          <Text style={styles.hint}>
            You'll be asked to review and confirm before anything is sent.
          </Text>
          {isValid && !testedCurrentMessage && (
            <Text style={styles.warningTxt}>
              You haven't sent a test of this exact message yet.
            </Text>
          )}
          <View style={styles.inlineRow}>
            <ActionButton
              text="Review & send to all users…"
              icon="megaphone-outline"
              variant="danger"
              onPress={openConfirm}
              disabled={!isValid}
            />
          </View>
        </View>

        <View style={[styles.card, { marginBottom: 50 }]}>
          <View style={styles.historyHeader}>
            <Text style={styles.stepTitle}>Recent broadcasts</Text>
            <Pressable onPress={fetchHistory}>
              <Ionicons name="refresh" size={20} color={EDColors.topbar} />
            </Pressable>
          </View>
          {historyLoading ? (
            <ActivityIndicator color={EDColors.gradient[1]} />
          ) : history.length === 0 ? (
            <Text style={styles.hint}>No broadcasts sent yet.</Text>
          ) : (
            history.map((item) => (
              <View key={item.id} style={styles.historyItem}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.historyTitle}>{item.title}</Text>
                  <Text style={styles.historyBody}>{item.body}</Text>
                  {!!item.link && (
                    <Text style={styles.historyLink} numberOfLines={1}>{item.link}</Text>
                  )}
                </View>
                <View style={styles.historyMeta}>
                  <Text style={[styles.status, item.status === 'sent' ? styles.resultSuccess : styles.resultError]}>
                    {item.status}
                  </Text>
                  <Text style={styles.historyMetaTxt}>{formatDate(item.createdAt)}</Text>
                  <Text style={styles.historyMetaTxt}>{item.sentBy}</Text>
                </View>
              </View>
            ))
          )}
        </View>
      </ScrollView>

      {confirmOpen && (
        <View style={styles.modalBg}>
          <View style={styles.modal}>
            <View style={styles.modalWarning}>
              <Ionicons name="warning" size={22} color={EDColors.red} />
              <Text style={styles.modalWarningTxt}>
                This will send a push notification to every user. It can't be undone.
              </Text>
            </View>

            <NotificationPreview title={trimmedTitle} body={trimmedBody} link={trimmedLink} />

            {duplicate && (
              <Text style={[styles.warningTxt, { marginTop: 12 }]}>
                This exact message was already sent on {formatDate(duplicate.createdAt)}.
              </Text>
            )}

            <Text style={[styles.label, { marginTop: 20 }]}>
              Type <Text style={{ fontWeight: 'bold' }}>{CONFIRMATION_PHRASE}</Text> to confirm
            </Text>
            <TextInput
              style={styles.input}
              value={confirmation}
              onChangeText={setConfirmation}
              autoCapitalize="characters"
              autoFocus
              editable={!sending}
              onSubmitEditing={handleSendToAll}
            />

            <View style={styles.modalButtons}>
              <ActionButton text="Cancel" variant="secondary" onPress={closeConfirm} disabled={sending} />
              <View style={{ width: 12 }} />
              <ActionButton
                text={sending ? 'Sending…' : 'Send to all users'}
                icon="megaphone-outline"
                variant="danger"
                onPress={handleSendToAll}
                disabled={confirmation !== CONFIRMATION_PHRASE || sending}
              />
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

export default Broadcast;
